/**
 * @file   src\Pages\SignIn.tsx
 * @brief  SignIn page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { React,useState, Button, Link, useNavigate, useEffect } from '../utils/thirdpartyComponents';
import Carwashlogo from '../assets/images/CarWash-Logo.svg';
import CWInput from '../components/CWInput';
import CWPasswordInput from '../components/CWPasswordInput';
import '../styles/signin.scss';
import * as Yup from 'yup'
import { EMAIL_MAX_LENGTH, PASSWORD_MAX_LENGTH } from '../utils/constants';
import { SIGNIN_SCHEMA } from '../validations/authSchema';
import { pageURLs } from '../utils/pageUrls';
import { useAppDispatch, useAppSelector } from '../hooks';
import { validateUser } from '../store/actions/authAction';
import { RootState } from '../store';
import { resetAuthState } from '../store/slices/authSlice';
import { MessageToaster } from '../utils/toastUtil';
import SmallLoader from '../components/SmallLoader';
import Cookies from 'js-cookie';

const SignIn = () => {
    // Declare action dispatch.
    const dispatch = useAppDispatch();

    // Toast object creation.
    const toast = new MessageToaster();

      
    const navigate = useNavigate()
    // Initialize states for formdata
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        adminUserType:'SUPERADMIN'

    })
    

    // Handle errors
    const [errors, setErrors] = useState({
        email: '',
        password: '',
    });
     // Access auth redux state variables.
    const { isSuccess, userInfo,isLoading, errorCode, errorMessage} = useAppSelector((state: RootState) => state.adminAuth);
    const [loading, setLoading] = useState(false);

    //   initial loading
    useEffect(() => {
        dispatch(resetAuthState());
        localStorage.clear()
        }, []);

    //  login success change function.
    useEffect(() => {
        if (isSuccess && userInfo?.accessToken) {
            const { accessToken, refreshToken, ...userInfoWithoutTokens } = userInfo;
            Cookies.set("accessToken", userInfo.accessToken, { expires: 365 * 10 });
            Cookies.set("refreshToken", userInfo.refreshToken, { expires: 365 * 10 });
            localStorage.setItem("CARWASH_ADMIN_DATA",JSON.stringify(userInfoWithoutTokens));
            localStorage.removeItem("CARWASH_OWNER_DATA");//Remove carwash super admin session
            if(userInfo?.userTypeId == 2 && userInfo?.permissionType == 2)
            {
                navigate(pageURLs.manage_carwash)
            }
            else{
                navigate(pageURLs.dashboard)
            }
           
        }
    }, [isSuccess]);

      // Show toast error message if login failed.
  useEffect(() => {
    if (errorMessage && errorCode) {
        setFormData({
            ...formData,
           'password': '',
        });
      toast.toastError(errorMessage);
    }
    
  }, [errorCode, errorMessage]);

    // Triggers on entering input
    const onInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        setFormData({
            ...formData,
            [name]: value.trim(),
        });

    };


    // Triggers when sign in is done
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        try {
            setLoading(true); // Show loader
            // Validate form data
            await SIGNIN_SCHEMA.validate(formData, { abortEarly: false });
            dispatch(validateUser(formData));
            setLoading(false); // Show loader

        } catch (error) {
            setLoading(false); // Show loader
            //If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                const newErrors: any = {};
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setErrors(newErrors);
            }
        }

    }

    return (
        <div>
            <div className="signin-bg"></div>
            <div className="signin-wrap d-flex">
                <div className="signin-container d-grid">
                    <div className="signin-hero d-flex align-items-start flex-column justify-content-center">
                        <img src={Carwashlogo} alt="Car Wash App" />
                        <h1>Super Admin</h1>
                    </div>
                    <div className="signin-form-wrap">
                        <h2>Sign In</h2>
                        <form onSubmit={e => handleSubmit(e)}>
                            <CWInput
                                id="email"
                                name="email"
                                type="text"
                                label="Email Address"
                                value={formData.email}
                                isMandatory
                                autoFocus={true}
                                maxLength={EMAIL_MAX_LENGTH}
                                errorMessage={errors.email && errors.email}
                                onChange={onInputChange}
                                autoComplete="email"
                            />
                            <CWPasswordInput
                                id="password"
                                name="password"
                                type={'password'}
                                label="Password"
                                placeholder=""
                                showpass={<span />}
                                value={formData.password}
                                maxLength={PASSWORD_MAX_LENGTH}
                                errorMessage={errors.password && errors.password}
                                isMandatory
                                onChange={onInputChange}
                            />
                            <div className="forgot text-end"><Link to={`/superadmin/${pageURLs.forgotpassword_step1}`} title="Forgot Password" >Forgot Password?</Link></div>
                            <Button
                                title='Sign In'
                                type="submit"
                                variant="primary"
                                className="primary-btn"
                            >
                                Sign In
                            </Button>
                        </form>
                        {/* <div className="new-user text-center">New User ?  <Link onClick={() => alert("Not Implemented")} to={""} title="Sign In" >Sign Up</Link></div> */}
                    </div>
                </div>
            </div>
            {(isLoading ) && "Loading....."}
            {loading && <SmallLoader />}
        </div>
    )
}

export default SignIn;
